import React from 'react';
import { Typography, styled, keyframes, LinearProgress } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Psychology as PsychologyIcon } from '@mui/icons-material';

interface ProcessingIndicatorProps {
  agentName?: string;
  message?: string;
}

const pulse = keyframes`
  0% {
    opacity: 0.6;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.05);
  }
  100% {
    opacity: 0.6;
    transform: scale(1);
  }
`;

const rotate = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

const ProcessingContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(3),
  backgroundColor: alpha(theme.palette.primary.main, 0.05),
  borderRadius: theme.shape.borderRadius,
  marginBottom: theme.spacing(2),
  maxWidth: '70%',
  marginLeft: 'auto',
  marginRight: 'auto',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
}));

const IconContainer = styled('div')(({ theme }) => ({
  position: 'relative',
  marginBottom: theme.spacing(2),
}));

const ThinkingIcon = styled(PsychologyIcon)(({ theme }) => ({
  fontSize: 48,
  color: theme.palette.primary.main,
  animation: `${pulse} 2s ease-in-out infinite`,
}));

const OrbitDot = styled('div')(({ theme }) => ({
  position: 'absolute',
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: theme.palette.secondary.main,
  top: '50%',
  left: '50%',
  marginTop: -4,
  marginLeft: -4,
  '&::before': {
    content: '""',
    position: 'absolute',
    width: 40,
    height: 40,
    border: `2px solid ${alpha(theme.palette.primary.main, 0.3)}`,
    borderRadius: '50%',
    top: '50%',
    left: '50%',
    marginTop: -20,
    marginLeft: -20,
    animation: `${rotate} 3s linear infinite`,
  },
}));

const ProcessingText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontWeight: 500,
  marginBottom: theme.spacing(1),
}));

const StatusText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '0.875rem',
  fontStyle: 'italic',
}));

const StyledLinearProgress = styled(LinearProgress)(({ theme }) => ({
  width: '100%',
  maxWidth: 200,
  marginTop: theme.spacing(2),
  height: 3,
  borderRadius: 1.5,
  backgroundColor: alpha(theme.palette.primary.main, 0.1),
  '& .MuiLinearProgress-bar': {
    backgroundColor: theme.palette.primary.main,
  },
}));

const ProcessingIndicator: React.FC<ProcessingIndicatorProps> = ({
  agentName,
  message
}) => {
  const messages = [
    "Analyzing your request...",
    "Thinking about the best response...",
    "Processing information...",
    "Searching knowledge base...",
    "Formulating response...",
    "Gathering insights...",
    "Consulting the knowledge graph...",
    "Synthesizing information..."
  ];

  const [currentMessageIndex, setCurrentMessageIndex] = React.useState(0);

  React.useEffect(() => {
    const interval = setInterval(() => {
      setCurrentMessageIndex((prev) => (prev + 1) % messages.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [messages.length]);

  const displayMessage = message || messages[currentMessageIndex];

  return (
    <ProcessingContainer>
      <IconContainer>
        <ThinkingIcon />
        <OrbitDot />
      </IconContainer>

      <ProcessingText variant="body1">
        {agentName ? `${agentName} is thinking...` : 'Processing your request...'}
      </ProcessingText>

      <StatusText variant="body2">
        {displayMessage}
      </StatusText>

      <StyledLinearProgress variant="indeterminate" />
    </ProcessingContainer>
  );
};

export default ProcessingIndicator;
